import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Typography, CircularProgress, Paper, Stack } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { signInWithPopup, GoogleAuthProvider, User, AuthError } from 'firebase/auth';
import { auth } from '@/firebase/firebase';
import { errorReporter } from '@/services/errorReportingService';
import { RouteMap } from '@/routes/Routes';
import noteGoIcon from '@/assets/icon_512x512@2x.png';

interface DesktopTokenResponse {
  customToken: string;
}

// SessionId format validation - alphanumeric with hyphens, max 128 chars
const SESSION_ID_PATTERN = /^[a-zA-Z0-9-]{1,128}$/;

/**
 * Validates sessionId format to prevent malformed deep links
 */
function isValidSessionId(id: string | null): id is string {
  return id !== null && SESSION_ID_PATTERN.test(id);
}

/**
 * Type guard for Firebase AuthError
 * Firebase auth errors have a 'code' property that starts with 'auth/'
 */
function isFirebaseAuthError(error: unknown): error is AuthError {
  return (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string' &&
    (error as { code: string }).code.startsWith('auth/')
  );
}

/**
 * Maps technical errors to user-friendly messages
 */
function getUserFriendlyError(err: unknown): string {
  if (err instanceof Error) {
    const message = err.message.toLowerCase();

    // User cancelled the sign-in
    if (message.includes('popup-closed') || message.includes('cancelled')) {
      return 'Sign-in was cancelled. Please try again.';
    }

    // Network issues
    if (message.includes('network') || message.includes('failed to fetch') || message.includes('offline')) {
      return 'Unable to connect. Please check your internet connection and try again.';
    }

    // Popup blocked
    if (message.includes('popup-blocked')) {
      return 'Pop-up was blocked. Please allow pop-ups for this site and try again.';
    }

    // API errors
    if (message.includes('api error')) {
      return 'Unable to complete sign-in. Please try again or contact support.';
    }
  }

  return 'Unable to complete sign-in. Please try again or contact support if the problem persists.';
}

type AuthStatus = 'idle' | 'loading' | 'success' | 'error';

const DesktopAuth = (): React.ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');

  const [status, setStatus] = useState<AuthStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const authAttemptedRef = useRef(false);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    };
  }, []);

  // Back on this page, a pending "Continue with email" redirect is stale (the user
  // backed out of /login) — drop it so a later login in this tab can't replay the
  // handoff. The normal return from /login already consumed it.
  useEffect(() => {
    if (sessionStorage.getItem('redirectAfterLogin')?.startsWith(RouteMap.DESKTOP_AUTH)) {
      sessionStorage.removeItem('redirectAfterLogin');
    }
  }, []);

  // Early validation: show error immediately if sessionId is missing or invalid
  useEffect(() => {
    if (!isValidSessionId(sessionId)) {
      setError('Invalid or missing session ID. Please try again from the desktop app.');
      setStatus('error');
    }
  }, [sessionId]);

  const handleAuthSuccess = useCallback(
    async (user: User) => {
      // Prevent duplicate auth attempts
      if (authAttemptedRef.current) return;
      authAttemptedRef.current = true;

      // Validate sessionId (should already be caught by early validation)
      if (!isValidSessionId(sessionId)) {
        setError('Invalid or missing session ID. Please try again from the desktop app.');
        setStatus('error');
        return;
      }

      setStatus('loading');
      try {
        // Get the ID token
        const idToken = await user.getIdToken();

        // Call API to exchange ID token for a custom token
        const apiUrl = import.meta.env.VITE_API_URL;

        if (!apiUrl) {
          errorReporter.critical('VITE_API_URL not configured', new Error('Missing API URL'), {
            feature: 'auth',
            operation: 'createDesktopToken',
          });
          setError('Configuration error. Please contact support.');
          setStatus('error');
          return;
        }

        const response = await fetch(`${apiUrl}/auth/create-desktop-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
          // Log detailed API error for debugging
          let errorDetail = '';
          try {
            const errorBody = await response.json();
            errorDetail = errorBody.detail || errorBody.message || JSON.stringify(errorBody);
          } catch {
            errorDetail = await response.text().catch(() => 'Unable to read response');
          }

          errorReporter.critical('Desktop token API request failed', new Error(errorDetail), {
            feature: 'auth',
            operation: 'createDesktopToken',
            status: response.status,
            statusText: response.statusText,
            hasSessionId: Boolean(sessionId),
          });

          throw new Error(`API error: ${response.status}`);
        }

        // Validate response structure
        let responseData: unknown;
        try {
          responseData = await response.json();
        } catch (parseError) {
          errorReporter.critical('Desktop token response not valid JSON', parseError, {
            feature: 'auth',
            operation: 'createDesktopToken',
          });
          throw new Error('Invalid response from server');
        }

        // Ensure the response is a non-null object before accessing properties
        if (typeof responseData !== 'object' || responseData === null) {
          errorReporter.critical('Desktop token response is not an object', new Error('Invalid response shape'), {
            feature: 'auth',
            operation: 'createDesktopToken',
            responseType: typeof responseData,
          });
          throw new Error('Invalid response from server');
        }

        const typedResponse = responseData as DesktopTokenResponse;
        if (!typedResponse.customToken || typeof typedResponse.customToken !== 'string') {
          errorReporter.critical('Desktop token missing from response', new Error('customToken field missing'), {
            feature: 'auth',
            operation: 'createDesktopToken',
            responseKeys: Object.keys(responseData as Record<string, unknown>),
          });
          throw new Error('Invalid token received');
        }

        const { customToken } = typedResponse;

        // Redirect back to the desktop app with the custom token
        const redirectUrl = `notego-desktop://auth-callback?token=${encodeURIComponent(customToken)}&sessionId=${encodeURIComponent(sessionId)}`;

        setStatus('success');

        // Small delay to show success message, then redirect to desktop app
        redirectTimeoutRef.current = setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1000);
      } catch (err) {
        errorReporter.critical('Desktop auth token exchange failed', err, {
          feature: 'auth',
          operation: 'createDesktopToken',
          hasSessionId: Boolean(sessionId),
        });
        setError(getUserFriendlyError(err));
        setStatus('error');
      }
    },
    [sessionId],
  );

  // Auto-redirect if already logged in (only if sessionId is valid)
  useEffect(() => {
    if (!isValidSessionId(sessionId)) return; // Don't attempt auth without valid sessionId

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && status === 'idle') {
        handleAuthSuccess(user);
      }
    });
    return () => unsubscribe();
  }, [handleAuthSuccess, status, sessionId]);

  const handleGoogleSignIn = async () => {
    setStatus('loading');
    setError(null);
    authAttemptedRef.current = false; // Reset for manual sign-in attempt

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await handleAuthSuccess(result.user);
    } catch (err) {
      // Use type guard to safely check for Firebase auth errors
      if (isFirebaseAuthError(err)) {
        // User-initiated cancellation - log as expected, not critical
        if (err.code === 'auth/popup-closed-by-user') {
          errorReporter.expected('User closed sign-in popup', err, { feature: 'auth' });
        } else if (err.code === 'auth/popup-blocked') {
          errorReporter.expected('Browser blocked sign-in popup', err, { feature: 'auth' });
        } else {
          errorReporter.critical('Firebase auth error during desktop sign-in', err, {
            feature: 'auth',
            operation: 'googleSignIn',
            firebaseCode: err.code,
          });
        }
      } else {
        errorReporter.critical('Unexpected sign-in error', err, {
          feature: 'auth',
          operation: 'googleSignIn',
        });
      }

      setError(getUserFriendlyError(err));
      setStatus('error');
    }
  };

  // Email users sign in on the real /login form, where the browser's password
  // manager (Safari/Apple Passwords, Chrome, 1Password, Bitwarden) can fill it —
  // the desktop app's own form gets no autofill. Login.tsx's redirect effect
  // brings them back here, and the onAuthStateChanged handoff above takes over.
  const handleEmailSignIn = () => {
    if (!isValidSessionId(sessionId)) return;
    sessionStorage.setItem('redirectAfterLogin', `${RouteMap.DESKTOP_AUTH}?sessionId=${sessionId}`);
    navigate(`${RouteMap.LOGIN}?action=login`);
  };

  // The desktop app opens the OS default browser, which may not be the one the
  // user is signed in on. The callback isn't tied to a browser, so this same
  // link finishes the handoff from any of them.
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  const handleRetry = () => {
    authAttemptedRef.current = false;
    setError(null);
    setStatus('idle');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#001C53',
        p: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 5,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            width: 88,
            height: 88,
            margin: '0 auto 24px',
            borderRadius: '20px',
            backgroundColor: '#0A1628',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <img
            src={noteGoIcon}
            alt="NoteGo"
            style={{
              width: 88,
              height: 88,
              objectFit: 'contain',
              borderRadius: '20px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
            }}
          />
        </Box>

        <Typography variant="h5" fontWeight={600} mb={1}>
          NoteGo Desktop
        </Typography>

        <Typography color="text.secondary" mb={4}>
          Sign in to connect your desktop app
        </Typography>

        {status === 'idle' && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleGoogleSignIn}
            startIcon={<GoogleIcon />}
            sx={{
              py: 1.5,
              backgroundColor: '#fff',
              color: '#333',
              border: '1px solid #ddd',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Continue with Google
          </Button>
        )}

        {status === 'idle' && isValidSessionId(sessionId) && (
          <>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleEmailSignIn}
              startIcon={<EmailOutlinedIcon />}
              sx={{ py: 1.5, mt: 2 }}
            >
              Continue with email
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              Signed in to NoteGo on a different browser?
            </Typography>
            <Button variant="text" onClick={handleCopyLink} startIcon={<ContentCopyIcon />} sx={{ mt: 0.5 }}>
              {copyState === 'copied' ? 'Link copied — paste it in that browser' : 'Copy this link'}
            </Button>
            {copyState === 'failed' && (
              <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                Could not copy automatically — copy the address bar instead.
              </Typography>
            )}
          </>
        )}

        {status === 'loading' && (
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography color="text.secondary">Authenticating...</Typography>
          </Stack>
        )}

        {status === 'success' && (
          <Stack alignItems="center" spacing={2}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
            <Typography color="success.main" fontWeight={500}>
              Authentication successful!
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Redirecting to NoteGo Desktop...
            </Typography>
          </Stack>
        )}

        {status === 'error' && (
          <Stack alignItems="center" spacing={2}>
            <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
            <Typography color="error.main" fontWeight={500}>
              Authentication failed
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {error}
            </Typography>
            <Button variant="outlined" onClick={handleRetry} sx={{ mt: 2 }}>
              Try Again
            </Button>
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 4 }}>
          This will securely connect your NoteGo Desktop app
        </Typography>
      </Paper>
    </Box>
  );
};

export default DesktopAuth;
